<template>
  <BaseSimpleLayout class="role-permission-edit-detail">
    <template #title>ロール詳細</template>
    <template #button-area>
      <div class="role-permission-edit-detail__actions">
        <el-button type="info" @click="router.push('/master/role-permission')">
          戻る
        </el-button>
        <ScreenPermissionGate code="ROLE_PERMISSION_SETTINGS-BTN_APPROVE">
          <el-button
            v-if="!isSelfRole"
            type="success"
            :disabled="isLoading || isApproving"
            @click="approveDraft"
          >
            承認
          </el-button>
        </ScreenPermissionGate>
        <ScreenPermissionGate code="ROLE_PERMISSION_SETTINGS-BTN_EDIT">
          <el-button
            v-if="!isSelfRole"
            type="primary"
            :disabled="isLoading"
            @click="goEditPage"
          >
            編集
          </el-button>
        </ScreenPermissionGate>
        <ScreenPermissionGate code="ROLE_PERMISSION_SETTINGS-BTN_CREATE">
          <el-button
            v-if="canCreateRole"
            :disabled="isLoading"
            @click="openDuplicatePage"
          >
            複製
          </el-button>
        </ScreenPermissionGate>
        <ScreenPermissionGate code="ROLE_PERMISSION_SETTINGS-BTN_DELETE">
          <el-button
            v-if="!isSelfRole && canDeleteRole"
            type="danger"
            :disabled="isLoading || isDeleting"
            @click="deleteCurrentRole"
          >
            削除
          </el-button>
        </ScreenPermissionGate>
        <el-button type="info" :disabled="isLoading" @click="openHistoryDrawer">
          編集履歴
        </el-button>
      </div>
    </template>

    <template #main>
      <div v-loading="isLoading" class="edit-form">
        <div class="edit-form__info">
          <el-form label-width="150px">
            <el-form-item label="ロールコード">
              <el-input :model-value="roleDetail?.code ?? ''" disabled />
            </el-form-item>
            <el-form-item label="ロール名">
              <el-input :model-value="roleDetail?.name ?? ''" disabled />
            </el-form-item>
            <el-form-item label="ホーム画面適用中">
              <el-tag type="info" class="home-path-readonly__tag">
                {{ detailHomePathCurrentLabel }}
              </el-tag>
            </el-form-item>
            <el-form-item label="ホーム画面仮登録">
              <el-tag
                v-if="detailHomePathDraft"
                type="warning"
                class="home-path-readonly__tag"
              >
                {{ detailHomePathDraftLabel }}
              </el-tag>
              <span v-else class="managed-roles-readonly__empty">—</span>
            </el-form-item>
            <el-form-item label="ロール種別">
              <el-radio-group :model-value="roleDetail?.level ?? 1" disabled>
                <el-radio v-if="roleDetail?.level === 3" :label="3">
                  スーパー管理者
                </el-radio>
                <el-radio :label="2">管理者</el-radio>
                <el-radio :label="1">担当者</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item v-if="showParentRoles" label="親ロール">
              <div class="managed-roles-readonly">
                <template v-if="parentRolesDisplay.length">
                  <el-tag
                    v-for="r in parentRolesDisplay"
                    :key="r.id"
                    type="info"
                    class="managed-roles-readonly__tag"
                  >
                    {{ r.name }}
                  </el-tag>
                </template>
                <span v-else class="managed-roles-readonly__empty">—</span>
              </div>
            </el-form-item>
            <el-form-item v-if="isManagedRoleLevel" label="管理対象担当者">
              <div class="managed-roles-readonly">
                <template v-if="managedRolesDisplay.length">
                  <el-tag
                    v-for="r in managedRolesDisplay"
                    :key="r.id"
                    type="info"
                    class="managed-roles-readonly__tag"
                  >
                    {{ r.name }}
                  </el-tag>
                </template>
                <span v-else class="managed-roles-readonly__empty">—</span>
              </div>
            </el-form-item>
          </el-form>
        </div>
        <div class="edit-form__table">
          <el-table
            :data="permissionRows"
            row-key="id"
            :tree-props="{ children: 'children' }"
            :cell-style="getCellStyle"
            border
            stripe
            :max-height="tableHeight"
            class="permission-table"
          >
            <el-table-column
              prop="name"
              label="機能（画面一覧）"
              fixed
              min-width="320"
            />
            <el-table-column column-key="main" width="190" align="center">
              <template #header>
                <span class="permission-table__main-header">Main 適用中</span>
              </template>
              <template #default="scope">
                <RolePermissionMatrixCell
                  :row-kind="scope.row.kind"
                  :model-value="scope.row.main"
                  :status="scope.row.main"
                  :options="permissionOptions"
                  :changed="false"
                  mode="view"
                />
              </template>
            </el-table-column>
            <el-table-column
              label="Draft 仮登録"
              column-key="draft"
              width="190"
              align="center"
            >
              <template #default="scope">
                <RolePermissionMatrixCell
                  :row-kind="scope.row.kind"
                  :model-value="scope.row.draft"
                  :status="scope.row.draft"
                  :options="permissionOptions"
                  :changed="false"
                  mode="view"
                />
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <RolePermissionEditHistoryDrawer
        v-model="historyDrawerVisible"
        :items="editHistoryVisible"
        :has-more="hasMoreHistory"
        :loading="historyLoading"
        :loading-more="historyLoadingMore"
        @open="onHistoryDrawerOpen"
        @load-more="loadMoreHistory"
      />
      <BaseConfirmDialog
        v-model="approveDialogVisible"
        title="承認の確認"
        message="Draft 仮登録 の内容を Main 適用中 に反映します。よろしいですか？"
        confirm-text="承認"
        cancel-text="キャンセル"
        confirm-type="primary"
        :loading="isApproving"
        @confirm="confirmApproveDraft"
      />
      <BaseConfirmDialog
        v-model="deleteDialogVisible"
        title="削除の確認"
        message="このロールを削除します。よろしいですか？"
        confirm-text="削除"
        cancel-text="キャンセル"
        confirm-type="danger"
        :loading="isDeleting"
        @confirm="confirmDeleteRole"
      />
    </template>
  </BaseSimpleLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { ElMessage } from 'element-plus';
  import { useRoute } from 'vue-router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import RolePermissionMatrixCell from '@/pages/master/role-permission/RolePermissionMatrixCell.vue';
  import { buildCellStyle } from '@/pages/master/role-permission/cellStyle';
  import RolePermissionEditHistoryDrawer from '@/pages/master/role-permission/RolePermissionEditHistoryDrawer.vue';
  import {
    DEFAULT_ROLE_HOME_PATH,
    homePathSelectLabel
  } from '@/pages/master/role-permission/homePathConfig';
  import { buildParentRoleIdsForDisplay } from '@/pages/master/role-permission/parentRoleHelpers';
  import type { EditHistoryItem } from '@/pages/master/role-permission/EditHistoryLog.vue';
  import router from '@/router';
  import store from '@/store';
  import type {
    ActionKey,
    RolePermissionDetail,
    RolePermissionRole,
    RolePermissionScreen
  } from '@/store/modules/rolePermission';

  type PermissionValue = 'full' | 'edit' | 'view' | 'limited' | 'none';
  type PermissionRow = {
    id: string;
    name: string;
    kind: 'screen' | 'button';
    screenId?: number;
    main: PermissionValue;
    draft: PermissionValue;
    children?: PermissionRow[];
  };

  const route = useRoute();
  const roleId = computed(() => Number(route.params.roleId ?? 0));
  const tableHeight = window.innerHeight - 260;
  const roleDetail = ref<RolePermissionDetail | null>(null);
  const permissionRows = ref<PermissionRow[]>([]);
  const historyDrawerVisible = ref(false);
  const historyLoading = ref(false);
  const historyLoadingMore = ref(false);
  const editHistoryVisible = ref<EditHistoryItem[]>([]);
  const historyNext = ref<number | null>(null);
  const screenNameMap = ref<Record<number, string>>({});
  const isLoading = ref(false);
  const isApproving = ref(false);
  const isDeleting = ref(false);
  const approveDialogVisible = ref(false);
  const deleteDialogVisible = ref(false);
  /** Resolved names for `managedChildRoleIds` (管理者/スーパー管理者ロール). */
  const managedRolesDisplay = ref<{ id: number; name: string }[]>([]);
  const parentRolesDisplay = ref<{ id: number; name: string }[]>([]);
  const detailHomePathCurrent = computed(() => {
    const path = roleDetail.value?.homePath?.trim();
    return path || DEFAULT_ROLE_HOME_PATH;
  });
  const detailHomePathCurrentLabel = computed(() =>
    homePathSelectLabel(detailHomePathCurrent.value)
  );
  const detailHomePathDraft = computed(
    () => roleDetail.value?.homePathDraft?.trim() ?? ''
  );
  const detailHomePathDraftLabel = computed(() =>
    homePathSelectLabel(detailHomePathDraft.value)
  );
  const showParentRoles = computed(
    () => roleDetail.value?.level === 1 || roleDetail.value?.level === 2
  );
  const isManagedRoleLevel = computed(
    () => roleDetail.value?.level === 2 || roleDetail.value?.level === 3
  );
  const currentAuthRoleId = computed(() =>
    Number(store.getters['auth/auth'].role ?? 0)
  );
  const isSelfRole = computed(
    () => roleDetail.value?.id === currentAuthRoleId.value
  );
  const canDeleteRole = computed(() => roleDetail.value?.canDeleted === true);
  // 担当者（roleLevel = 1）はロールを複製（追加）できない。
  const canCreateRole = computed(
    () => Number(store.getters['auth/auth'].roleLevel ?? 0) !== 1
  );

  /** Same scope as matrix list: own role + managed child roles. Otherwise treat like unknown URL → /top. */
  const isRoleInCurrentUserScope = (
    rolesList: RolePermissionRole[],
    targetRoleId: number
  ): boolean => {
    const viewerRole = rolesList.find((r) => r.id === currentAuthRoleId.value);
    if (!viewerRole) return false;
    return (
      viewerRole.id === targetRoleId ||
      (viewerRole.managedChildRoleIds ?? []).includes(targetRoleId)
    );
  };

  const permissionOptions = [
    { value: 'full', label: '登録/訂正/削除可' },
    { value: 'edit', label: '訂正可' },
    { value: 'view', label: '閲覧のみ' },
    { value: 'none', label: '権限無し' }
  ] as { value: PermissionValue; label: string }[];

  const hasMoreHistory = computed(() => historyNext.value !== null);
  const actionKeyToPermission = (actionKey: ActionKey): PermissionValue => {
    if (actionKey === 'CRUD') return 'full';
    if (actionKey === 'EDIT') return 'edit';
    if (actionKey === 'VIEW') return 'view';
    return 'none';
  };
  const permissionToLabel = (value: PermissionValue): string => {
    if (value === 'full') return '登録/訂正/削除可';
    if (value === 'edit') return '訂正可';
    if (value === 'view') return '閲覧のみ';
    return '権限無し';
  };
  const getCellStyle = buildCellStyle<PermissionRow>((row, column) => {
    if (column.columnKey !== 'draft') return null;
    return { highlight: row.main !== row.draft, color: '#fff3bf' };
  });

  type DiffRow = {
    screenId?: number;
    to?: ActionKey;
    from?: ActionKey;
    oldActionKey?: ActionKey;
    newActionKey?: ActionKey;
  };
  type HistoryApiItem = {
    id: number;
    at: number;
    action: string;
    roleCode?: string;
    roleName?: string;
    editor?: string | null;
    diffPayload?: {
      updatedScreens?: DiffRow[];
      approvedPermissionChanges?: DiffRow[];
      permissionsDraft?: DiffRow[];
      permissionChanges?: DiffRow[];
      comment?: string;
    } | null;
  };
  const mapHistoryItem = (item: HistoryApiItem): EditHistoryItem => {
    const action =
      item.action === 'create' ||
      item.action === 'update' ||
      item.action === 'delete' ||
      item.action === 'approve'
        ? item.action
        : item.action === 'duplicate'
          ? 'create'
          : 'update';
    const payload = item.diffPayload ?? null;
    const mapped: EditHistoryItem = {
      id: String(item.id),
      at: Number(item.at),
      action,
      roleCode: item.roleCode,
      roleName: item.roleName,
      editor: item.editor ?? undefined,
      diffPayload: item.diffPayload ?? null
    };
    if (action === 'approve') {
      const updated = Array.isArray(payload?.approvedPermissionChanges)
        ? payload?.approvedPermissionChanges
        : Array.isArray(payload?.updatedScreens)
          ? payload?.updatedScreens
          : [];
      mapped.approvedPermissions = updated.map((row: DiffRow) => ({
        screen:
          screenNameMap.value[Number(row.screenId)] ??
          `Screen #${String(row.screenId)}`,
        permission: permissionToLabel(
          actionKeyToPermission(row.newActionKey ?? row.to ?? 'NONE')
        )
      }));
      mapped.notes = payload?.comment ?? '';
    }
    if (action === 'update') {
      const changes = Array.isArray(payload?.permissionChanges)
        ? payload?.permissionChanges
        : Array.isArray(payload?.permissionsDraft)
          ? payload?.permissionsDraft
          : [];
      mapped.changes = changes.map((row: DiffRow) => ({
        screen:
          screenNameMap.value[Number(row.screenId)] ??
          `Screen #${String(row.screenId)}`,
        oldValue: permissionToLabel(
          actionKeyToPermission(row.from ?? row.oldActionKey ?? 'NONE')
        ),
        newValue: permissionToLabel(
          actionKeyToPermission(row.to ?? row.newActionKey ?? 'NONE')
        )
      }));
    }
    return mapped;
  };
  const loadHistory = async (next?: number) => {
    const response = await store.dispatch('rolePermission/fetchEditHistory', {
      roleId: roleId.value,
      next,
      append: typeof next === 'number'
    });
    const mapped = (response.items ?? []).map(mapHistoryItem);
    editHistoryVisible.value =
      typeof next === 'number'
        ? [...editHistoryVisible.value, ...mapped]
        : mapped;
    historyNext.value = response.next ?? null;
  };
  const loadMoreHistory = async () => {
    if (!hasMoreHistory.value || historyLoadingMore.value) return;
    historyLoadingMore.value = true;
    try {
      await loadHistory(historyNext.value ?? undefined);
    } finally {
      historyLoadingMore.value = false;
    }
  };
  const openHistoryDrawer = () => {
    historyDrawerVisible.value = true;
  };
  const onHistoryDrawerOpen = async () => {
    historyLoading.value = true;
    try {
      await loadHistory();
    } finally {
      historyLoading.value = false;
    }
  };
  const goEditPage = () => {
    router.push({
      name: 'RolePermissionEdit',
      params: { roleId: String(route.params.roleId ?? '0') }
    });
  };
  const openDuplicatePage = () => {
    router.push({
      name: 'RolePermissionCreateDetail',
      query: { copyFrom: String(roleId.value) }
    });
  };
  const approveDraft = async () => {
    if (isSelfRole.value) {
      ElMessage.warning('自身のロールは承認できません。');
      return;
    }
    approveDialogVisible.value = true;
  };
  const confirmApproveDraft = async () => {
    isApproving.value = true;
    try {
      await store.dispatch('rolePermission/approveRole', {
        roleId: roleId.value
      });
      approveDialogVisible.value = false;
      ElMessage.success('Draft 仮登録 を Main 適用中 に反映しました。');
      await loadDetail();
      if (historyDrawerVisible.value) {
        await loadHistory();
      }
    } finally {
      isApproving.value = false;
    }
  };
  const deleteCurrentRole = () => {
    if (!canDeleteRole.value) return;
    deleteDialogVisible.value = true;
  };
  const confirmDeleteRole = async () => {
    isDeleting.value = true;
    try {
      await store.dispatch('rolePermission/deleteRole', roleId.value);
      deleteDialogVisible.value = false;
      ElMessage.success('ロールを削除しました。');
      await router.replace('/master/role-permission');
    } finally {
      isDeleting.value = false;
    }
  };

  const loadDetail = async (): Promise<boolean> => {
    const id = roleId.value;
    if (!Number.isFinite(id) || id <= 0) {
      await router.replace('/top');
      return false;
    }
    const [screens, roles] = await Promise.all([
      store.dispatch('rolePermission/fetchScreens'),
      store.dispatch('rolePermission/fetchRoles')
    ]);
    const typedScreens = screens as RolePermissionScreen[];
    const rolesList = roles as RolePermissionRole[];
    if (!isRoleInCurrentUserScope(rolesList, id)) {
      await router.replace('/top');
      return false;
    }
    const detail = await store.dispatch('rolePermission/fetchRoleDetail', id);
    const typedDetail = detail as RolePermissionDetail | null;
    if (!typedDetail) {
      await router.replace('/top');
      return false;
    }
    roleDetail.value = typedDetail;
    const managedIds: number[] = typedDetail?.managedChildRoleIds ?? [];
    managedRolesDisplay.value = managedIds.map((id: number) => {
      const match = rolesList.find((r) => r.id === id);
      return {
        id,
        name: match?.name ?? `ID: ${String(id)}`
      };
    });
    const parentIds = buildParentRoleIdsForDisplay(typedDetail, rolesList);
    parentRolesDisplay.value = parentIds.map((pid: number) => {
      const match = rolesList.find((r) => r.id === pid);
      return {
        id: pid,
        name: match?.name ?? `ID: ${String(pid)}`
      };
    });
    const mainMap = (typedDetail?.permissionsMain ?? []).reduce(
      (
        acc: Record<number, PermissionValue>,
        row: { screenId: number; actionKey: ActionKey }
      ) => ({
        ...acc,
        [row.screenId]: actionKeyToPermission(row.actionKey)
      }),
      {}
    );
    const draftMap = (typedDetail?.permissionsDraft ?? []).reduce(
      (
        acc: Record<number, PermissionValue>,
        row: { screenId: number; actionKey: ActionKey }
      ) => ({
        ...acc,
        [row.screenId]: actionKeyToPermission(row.actionKey)
      }),
      {}
    );
    screenNameMap.value = typedScreens.reduce(
      (acc, screen) => {
        acc[screen.id] = screen.name;
        screen.childs.forEach((child) => {
          acc[child.id] = child.name;
        });
        return acc;
      },
      {} as Record<number, string>
    );
    permissionRows.value = typedScreens.map((root) => ({
      id: String(root.id),
      name: root.name,
      kind: 'screen',
      screenId: root.id,
      main: mainMap[root.id] ?? 'none',
      draft: draftMap[root.id] ?? 'none',
      children: root.childs.map((child) => ({
        id: String(child.id),
        name: child.name,
        kind: 'button',
        screenId: child.id,
        main: mainMap[child.id] ?? 'none',
        draft: draftMap[child.id] ?? 'none'
      }))
    }));
    return true;
  };

  onMounted(async () => {
    isLoading.value = true;
    try {
      await loadDetail();
    } finally {
      isLoading.value = false;
    }
  });
</script>

<style lang="scss">
  .role-permission-edit-detail {
    .role-permission-edit-detail__actions {
      display: inline-flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-end;
    }

    .edit-form {
      display: flex;
      gap: 10%;
      padding-top: 16px;

      &__info {
        flex: 3 1 0;
        min-width: 0;

        .el-input {
          width: 100%;
        }
      }

      &__table {
        flex: 7 1 0;
        min-width: 0;
      }
    }

    .permission-table__main-header {
      font-weight: 700;
      color: var(--el-color-primary, #409eff);
    }

    .permission-table .permission-column .cell {
      padding: 0 !important;
    }

    .managed-roles-readonly {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      line-height: 1.5;
    }

    .managed-roles-readonly__tag {
      margin: 0;
    }

    .managed-roles-readonly__empty {
      color: var(--el-text-color-secondary, #909399);
    }

    .home-path-readonly__tag {
      margin: 0;
    }
  }
</style>
